import { AnswerType, IngestionFieldAnswer, IngestionFieldDefinition } from "./data.ingestion.service";
import { FieldType, isPrimitiveFieldType } from "./data.ingestion.const";

/** Field types that never participate in cross-form seeding. */
const NON_COPY_FIELD_TYPES = new Set<FieldType>(["FILE", "IMAGE", "SECTION"]);

function normalizeReportingCode(code: string | undefined): string {
    return (code ?? "").trim().toLowerCase();
}

function serializedSourceAnswer(source: IngestionFieldAnswer): string | undefined {
    const fieldDefinition = source.fieldDefinition;
    if (!fieldDefinition) return undefined;
    if (fieldDefinition.answerType === AnswerType.JSON) {
        const json = source.answerJson?.trim();
        if (json) return json;
    }
    const raw = source.answerRaw?.trim();
    if (raw) return raw;
    if (source.answerLargeString?.trim()) return source.answerLargeString.trim();
    if (source.answerString?.trim()) return source.answerString.trim();
    if (source.answerInteger != null) return String(source.answerInteger);
    if (source.answerFloat != null) return String(source.answerFloat);
    if (source.answerBoolean != null) return String(source.answerBoolean);
    if (source.answerDatetime) return source.answerDatetime;
    return undefined;
}

function activeTargetFields(targetFieldDefinitions: IngestionFieldDefinition[]): IngestionFieldDefinition[] {
    return [...targetFieldDefinitions]
        .filter(
            (fieldDefinition) =>
                !fieldDefinition.isDeleted &&
                fieldDefinition.fieldDefinitionId != null &&
                fieldDefinition.fieldType &&
                !NON_COPY_FIELD_TYPES.has(fieldDefinition.fieldType),
        )
        .sort((fieldA, fieldB) => {
            const orderA = fieldA.sortOrder ?? 0;
            const orderB = fieldB.sortOrder ?? 0;
            if (orderA !== orderB) return orderA - orderB;
            return (fieldA.fieldDefinitionId ?? 0) - (fieldB.fieldDefinitionId ?? 0);
        });
}

/**
 * Types where many {@link IngestionFieldAnswer} rows may map to the same target field (do not "consume" the slot).
 */
function targetFieldAllowsMultipleAnswerRows(fieldDefinition: IngestionFieldDefinition): boolean {
    const fieldType = fieldDefinition.fieldType;
    if (!fieldType) return false;
    if (fieldType === "MULTISELECT" || fieldType === "MULTISELECT_OCCURRENCES") return true;
    if (fieldType === "CHECKBOX" && fieldDefinition.answerType !== AnswerType.BOOLEAN) return true;
    if (
        fieldType === "CATEGORIES" ||
        fieldType === "REQUIREMENTS" ||
        fieldType === "ORGANIZATION_MULTISELECT" ||
        fieldType === "ORG_CATEGORIES"
    ) {
        return true;
    }
    if (
        (fieldType === "CUSTOM_ATTRIBUTE" || fieldType === "ORG_CUSTOM_ATTRIBUTE") &&
        fieldDefinition.custAtrb?.multiVal
    ) {
        return true;
    }
    return false;
}

function typeOnlyMatchEligible(fieldType: FieldType | undefined): boolean {
    if (!fieldType) return false;
    if (NON_COPY_FIELD_TYPES.has(fieldType)) return false;
    return !isPrimitiveFieldType(fieldType);
}

function buildPayloadAnswer(
    targetField: IngestionFieldDefinition,
    newIngestionPointId: number,
    contactId: number,
    answerRaw: string,
): IngestionFieldAnswer {
    return {
        fieldDefinitionId: targetField.fieldDefinitionId,
        fieldDefinition: { ...targetField, specialFieldItem: undefined } as IngestionFieldDefinition,
        contId: contactId,
        ingestionPointId: newIngestionPointId,
        answerRaw,
    };
}

/**
 * Map source submission answers onto target form field definitions using tiered rules:
 * (1) exact fieldDefinitionId, (2) fieldType + reportingFieldCode, (3) fieldType-only for non-primitive types.
 * Each target field is consumed at most once unless it {@link targetFieldAllowsMultipleAnswerRows}.
 */
export function matchSourceAnswersToTargetFields(
    sourceAnswers: IngestionFieldAnswer[],
    targetFieldDefinitions: IngestionFieldDefinition[],
    newIngestionPointId: number,
    contactId: number,
): IngestionFieldAnswer[] {
    const targets = activeTargetFields(targetFieldDefinitions);
    const consumedSingleSlotTargetIds = new Set<number>();
    const results: IngestionFieldAnswer[] = [];

    const canPickTargetField = (targetField: IngestionFieldDefinition): boolean =>
        targetFieldAllowsMultipleAnswerRows(targetField) ||
        !consumedSingleSlotTargetIds.has(targetField.fieldDefinitionId!);

    const considerSourceAnswer = (sourceAnswer: IngestionFieldAnswer) => {
        const sourceField = sourceAnswer.fieldDefinition;
        const sourceFieldId = sourceAnswer.fieldDefinitionId;
        if (sourceFieldId == null || sourceFieldId < 0 || !sourceField?.fieldType) {
            return;
        }
        if (NON_COPY_FIELD_TYPES.has(sourceField.fieldType)) {
            return;
        }
        const raw = serializedSourceAnswer(sourceAnswer);
        if (raw == null || raw === "") {
            return;
        }

        let matchedTarget: IngestionFieldDefinition | undefined;

        for (const targetField of targets) {
            if (sourceFieldId === targetField.fieldDefinitionId && canPickTargetField(targetField)) {
                matchedTarget = targetField;
                break;
            }
        }
        if (!matchedTarget) {
            const sourceCode = normalizeReportingCode(sourceField.reportingFieldCode);
            if (sourceCode.length > 0 && sourceField.fieldType) {
                for (const targetField of targets) {
                    if (targetField.fieldType !== sourceField.fieldType) continue;
                    if (normalizeReportingCode(targetField.reportingFieldCode) !== sourceCode) continue;
                    if (!canPickTargetField(targetField)) continue;
                    matchedTarget = targetField;
                    break;
                }
            }
        }
        if (!matchedTarget && typeOnlyMatchEligible(sourceField.fieldType)) {
            for (const targetField of targets) {
                if (targetField.fieldType !== sourceField.fieldType) continue;
                if (!typeOnlyMatchEligible(targetField.fieldType)) continue;
                if (!canPickTargetField(targetField)) continue;
                matchedTarget = targetField;
                break;
            }
        }

        if (matchedTarget) {
            if (!targetFieldAllowsMultipleAnswerRows(matchedTarget)) {
                consumedSingleSlotTargetIds.add(matchedTarget.fieldDefinitionId!);
            }
            results.push(buildPayloadAnswer(matchedTarget, newIngestionPointId, contactId, raw));
        }
    };

    for (const sourceAnswer of sourceAnswers) {
        considerSourceAnswer(sourceAnswer);
    }

    return results;
}
